import type { Prettify } from '@mnd-frontend/ts';
import { BREAKPOINTS, WEB_PURPLE_REG, WEB_WHITE_REG } from '@mnd-frontend/ui/shared/tokens';
import { type FormEventHandler, Fragment, type ReactNode } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Input from '../Input';
import Select from '../Select';
import Heading from '../typography/Heading';

const StyledForm = styled.form`
  background: ${WEB_WHITE_REG};
  padding: 1.5rem 1rem;
  border-radius: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  box-sizing: border-box;
  width: 100%;
  @media (min-width: ${BREAKPOINTS.LG}px) {
    gap: 2.5rem;
    padding: 2.5rem;
  }
`;

const FieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media (min-width: ${BREAKPOINTS.LG}px) {
    gap: 1.5rem;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
`;

const RichText = styled.div`
  font-size: 0.875rem;
  color: ${WEB_PURPLE_REG};

  a {
    font-weight: 700;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Appendix = styled.div`
  text-align: center;
  font-size: 0.875rem;

  a {
    font-weight: 600;
    text-decoration: underline;
  }
`;

type BaseFormField = {
  type: string;
  name: string;
  required: boolean;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  richText?: string;
  validate?: (value: string) => boolean;
};

type InputFormField = Prettify<
  BaseFormField & {
    label: string;
  }
>;

type SelectFormField = Prettify<
  BaseFormField & {
    type: 'select';
    label: string;
    options: Array<{ label: string; value: string }>;
  }
>;

type CheckboxFormField = Prettify<
  BaseFormField & {
    type: 'checkbox';
    label: ReactNode;
  }
>;

type CustomFormField = Prettify<
  BaseFormField & {
    type: 'custom';
    component: ReactNode;
  }
>;

type Field = SelectFormField | CheckboxFormField | InputFormField;
export type FormField = Field | CustomFormField;

type Props = {
  title?: string;
  fields: FormField[];
  errors: { [fieldName: string]: string };
  submitButtonText?: string;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  loading?: boolean;
  disabled?: boolean;
  appendix?: string | null;
};

const Form = ({
  title,
  fields,
  errors,
  submitButtonText = 'Submit',
  onSubmit,
  loading,
  disabled,
  appendix,
}: Props) => {
  const renderFields = (field: Field) => {
    switch (field.type) {
      case 'richText':
        if (field.richText) {
          return <RichText key={field.name} dangerouslySetInnerHTML={{ __html: field.richText }} />;
        } else {
          return null;
        }
      case 'checkbox':
        return (
          <Checkbox
            key={field.name}
            name={field.name}
            label={field.label}
            error={errors[field.name] ?? ''}
            defaultValue={field.defaultValue === 'true'}
          />
        );
      case 'select':
        return (
          <Select
            label={field.label}
            placeholder={field.placeholder ?? field.label}
            required={field.required}
            key={field.name}
            name={field.name}
            error={errors[field.name] ?? ''}
            onBlur={e => field.validate?.(e.currentTarget.value)}
            options={(field as SelectFormField).options}
          />
        );
      default:
        return (
          <Input
            key={field.name}
            name={field.name}
            type={field.type}
            label={(field as InputFormField).label}
            labelDescription={field.description}
            placeholder={field.placeholder}
            required={field.required}
            onBlur={e => field.validate?.(e.currentTarget.value)}
            error={errors?.[field.name]}
          />
        );
    }
  };
  return (
    <StyledForm onSubmit={onSubmit}>
      {title && (
        <Heading as="h2" isSmallFont textAlign="center">
          {title}
        </Heading>
      )}
      <FieldsWrapper>
        {fields.map(field => (
          <Fragment key={field.name}>
            {field.type === 'custom'
              ? (field as CustomFormField).component
              : renderFields(field as Field)}
          </Fragment>
        ))}
      </FieldsWrapper>
      <SubmitButton
        configuration="secondary"
        type="submit"
        loading={loading}
        disabled={loading || disabled}
      >
        {submitButtonText ?? 'Submit'}
      </SubmitButton>
      {appendix && <Appendix dangerouslySetInnerHTML={{ __html: appendix }} />}
    </StyledForm>
  );
};

export default Form;
